import React, { useEffect, useState } from "react";
import { withStyles } from "components/common";
import { InfoIcon } from "components/common/Svg";
import decode from "jwt-decode";
import { track } from "../../../utils/analytics";
import { connect } from "react-redux";

const pill = ({ index, tag, action, add = true } = {}) => {
  return (
    <div
      key={`tag #${index}`}
      className={`inline-flex items-center ${
        add ? "bg-gray-200" : "bg-green-200"
      } cursor-default select-none rounded px-2 mr-2`}
    >
      {tag}
      <button
        type="button"
        className="flex outline-0-important"
        onClick={action}
      >
        <i
          className={`fas ${
            add
              ? "fa-plus hover:text-green-primary"
              : "fa-times hover:text-red-500"
          } pr-1 ml-2`}
        />
      </button>
    </div>
  );
};

const Details = ({
  row,
  close,
  next,
  addTransactionTags,
  removeTransactionTag,
  roles,
}) => {
  track("DASHBOARD_TRANSACTIONS_DETAIL_SCREEN_LOADED");
  const {
    original: {
      status = "",
      disputed,
      dispute_status,
      dispute_resolved,
      settled_at,
      amount,
      tags,
      id,
    } = {},
  } = row;
  const [tagInput, setTagInput] = useState(false); // RENDERS CUSTOM TAG FIELD
  const [spinner, setSpinner] = useState(null); // CONDITIONAL RENDERS LOADING SPINNER
  const [error, setError] = useState(null); // DISPLAYS IF ERROR WHEN ADD/REM TAGS
  const [userTag, setUserTag] = useState(""); // USER INPUTTED TAG VALUE
  const [tagPayload, setTagPayload] = useState(tags || []); // HOLDS TXN TAG INFORMATION
  const [suggested, setSuggested] = useState([]); // HANDLED UPDATING TAG SUGGESTIONS

  useEffect(() => {
    if (Array.isArray(tagPayload) && roles.admin) {
      const suggestions = ["suspicious", "unauthorized"].filter(
        (val) => !tagPayload.includes(val),
      );
      setSuggested(suggestions);
    }
  }, [tagPayload]);

  const handleTagSubmit = (tagsToAdd) => {
    if (!addTransactionTags) {
      setError("submitting a tag");
    } else if (!tagsToAdd[0].trim().length) {
      setUserTag("");
      setError("tags cannot be empty");
    } else {
      setSpinner(true);
      setTagInput(false);
      addTransactionTags({ transactionId: id, tags: tagsToAdd }, (res) => {
        setTagPayload(res);
        setUserTag("");
        setSuggested((ps) => ps.filter((val) => !tagsToAdd.includes(val)));
        setSpinner(false);
      });
    }
  };

  const handleTagRemove = (tag) => {
    if (!removeTransactionTag) { return; }
    console.log(`attempting to remove tag:`, tag);
    setSpinner(true);
    removeTransactionTag(
      { transactionId: id, tag },
      (res) => {
        console.log("deleted successfully");
        setTagPayload(res);
        setSpinner(false);
        setUserTag("");
      },
      (updatedResults) => {
        setUserTag("");
        console.log(
          "deleted unsuccessfully with updatedResults:",
          updatedResults,
        );
        setSpinner(false);
        setError("deleting tags");
      },
    );
  };

  return (
    <div>
      <div className="pb-6">
        <p className="mb-6 font-title font-light text-xl">
          Transaction Details
        </p>
        <div className="mb-4">
          <p className="font-title text-l font-semibold">Description: </p>
          <p>{row.original.description}</p>
        </div>
        <div className="mb-4">
          <p className="font-title font-semibold">Amount: </p>
          <p>{`$${row.original.amount}`}</p>
        </div>
        <div className="mb-4">
          <p className="font-title font-semibold">Category: </p>
          <p>{row.original.category || "none"}</p>
        </div>
        <div className="mb-4">
          {settled_at && (
            <>
              <p className="font-title font-semibold">Settled Date:</p>
              <p>
                {new Date(settled_at).toLocaleString("en-US", {
                  timeZone: "UTC",
                  dateStyle: "medium",
                })}
              </p>
            </>
          )}
        </div>
        {disputed && (
          <div className="mb-4">
            <p className="font-title font-semibold">
              You have disputed this transaction
            </p>
            <p>
              <span>The dispute is currently </span>
              <span className="font-title font-semibold">{dispute_status}</span>
            </p>
          </div>
        )}
        {/* ADMIN + GODVIEW CHECK */}
        {roles.admin && addTransactionTags && removeTransactionTag && (
          <div className="mb-4">
            <p className="font-title font-semibold">Tags:</p>
            {spinner || (tagPayload || {}).length ? (
              <></>
            ) : (
              <div className="text-sm italic mb-2">
                no tags yet, add more below!
              </div>
            )}
            {tagPayload &&
              spinner !== true &&
              tagPayload.map((tag, i) =>
                pill({
                  index: i,
                  tag,
                  action: () => handleTagRemove(tag),
                  add: false,
                }),
              )}
            {/* ERROR HANDLING */}
            {error ? (
              <div className="flex flex-row mx-auto my-auto items-center">
                <div>{`Error ${error} for this transaction!`}</div>
                <button
                  onClick={() => setError(null)}
                  type="button"
                  className="py-1 px-1 ml-2"
                >
                  <i className="fas fa-redo text-red-500" />
                </button>
              </div>
            ) : (
              <div>
                {spinner ? (
                  <div className="mb-2 mt-2 pb-4 pt-2">
                    <div
                      className="loader h-auto mx-auto block"
                      style={{ width: 64, height: 64 }}
                    />
                  </div>
                ) : (
                  <div>
                    {suggested.length > 0 && (
                      <div className="pt-1">
                        {suggested.map((sugTag, i) =>
                          pill({
                            index: i,
                            tag: sugTag,
                            action: () => handleTagSubmit([sugTag]),
                          }),
                        )}
                      </div>
                    )}
                    {/* CUSTOM TAG BUTTON RENDERS CUSTOM TAG INPUT */}
                    {tagInput ? (
                      <div className="mt-2 flex items-center">
                        <span className="w-full">
                          <div className="relative text-gray-700">
                            <input
                              className="w-full h-10 pl-3 pr-16 text-base placeholder-gray-600 border rounded-md focus:outline-none"
                              type="text"
                              placeholder="add custom tag"
                              value={userTag}
                              onChange={(e) => setUserTag(e.target.value)}
                            />
                            <button
                              type="button"
                              onClick={() => handleTagSubmit([userTag])}
                              className="absolute inset-y-0 right-0 px-4 rounded-r-md border-l outline-0-important hover:text-green-primary"
                            >
                                  Add
                            </button>
                          </div>
                        </span>
                        <button
                          type="button"
                          className="relative outline-0-important"
                          onClick={() => setTagInput(!tagInput)}
                        >
                          <i className="fas fa-times hover:text-red-500 pr-1 ml-2" />
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setTagInput(!tagInput)}
                          className="py-2 bg-none focus:outline-none text-sm"
                        >
                            + custom tag
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div
          className="absolute w-full left-0 bottom-0 py-4 px-8 flex justify-between"
          style={{ backgroundColor: "#F1F6F7" }}
        >
          {disputed ? (
            <div className="flex items-center text-gray-500">
              <InfoIcon className="pr-1 text-black" />
              <p className="select-none">
                {dispute_resolved ? "Dispute Resolved" : "In Dispute"}
              </p>
            </div>
          ) : status.toLowerCase() === "settled" && amount > 0 ? (
            <div></div>
          ) : status.toLowerCase() === "disputed" ? (
            <div className="flex items-center text-gray-500">
              <InfoIcon className="pr-1 text-black" />
              <p className="select-none">In Dispute</p>
            </div>
          ) : amount > 0 ? (
            <div className="flex items-center">
              <InfoIcon color="gray" className="pr-1 text-black" />
              <p>Not yet settled</p>
            </div>
          ) : (
            <div className="flex items-center">
              <InfoIcon color="gray" className="pr-1 text-black" />
              <p>Dispute unavailable</p>
            </div>
          )}
          <button type="button" className="flex items-center" onClick={close}>
            <p className="pr-1">close</p>
            <p>x</p>
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  roles: state.user.roles,
});

export default connect(mapStateToProps)(withStyles(Details));
